import { Check, Search, X } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { TextInput } from '@my/ui';
import type { TimeMode } from '../lib/types';

/** Pill-row picker for the four task time modes. */
export function TimeModePicker({
  value,
  onChange,
}: {
  value: TimeMode;
  onChange: (next: TimeMode) => void;
}) {
  const options: { value: TimeMode; label: string; title: string }[] = [
    { value: 'Undefined', label: 'Any', title: 'Any time — no specific window' },
    { value: 'AM', label: 'AM', title: 'Morning — before midday' },
    { value: 'PM', label: 'PM', title: 'Afternoon — midday onwards' },
    { value: 'Exact', label: 'Exact', title: 'Exact time — picks a specific hour:minute' },
  ];
  // `min-h-9` matches TextInput's h-9 so this row baselines against
  // adjacent inputs in the same form grid.
  return (
    <div className="flex min-h-9 flex-wrap items-center gap-1.5">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            title={opt.title}
            className={
              'inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-medium transition-colors ' +
              (active
                ? 'border-purple-300 bg-purple-50 text-purple-700'
                : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50')
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Multi-line counterpart to @my/ui's TextInput; same floating-label shell. */
export function TextAreaInput({
  label,
  rows = 2,
  id,
  ...props
}: { label?: ReactNode } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const reactId = useId();
  const inputId = id ?? props.name ?? reactId;
  return (
    <div className="w-full">
      <div className="relative">
        <textarea
          id={inputId}
          rows={rows}
          placeholder={props.placeholder ?? ' '}
          className={[
            'peer block w-full rounded-sm border border-slate-300 bg-white px-2 pb-1 pt-2 text-sm font-semibold text-slate-900',
            'placeholder:text-xs placeholder:font-normal placeholder:italic placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-blue-500',
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
            'read-only:cursor-not-allowed read-only:bg-slate-50 read-only:text-slate-500',
          ].join(' ')}
          {...props}
        />
        {label && (
          <label
            htmlFor={inputId}
            className="absolute -top-2 left-3 bg-white px-1 text-xs text-slate-500"
          >
            {label}
          </label>
        )}
      </div>
    </div>
  );
}

/** Compact read-only label/value pair used in the page header row. */
/**
 * The order header's label-over-value cell. Every cell in the header uses the
 * same two fixed lines — a 16px label, then a 28px value line with its content
 * sitting on the bottom — so labels share one line across the row and values,
 * chips and buttons share another, whatever their size.
 */
export const HEADER_LABEL = 'block text-[10px] font-medium uppercase leading-4 tracking-wide text-slate-500';
export const HEADER_VALUE_ROW = 'flex min-h-7 flex-wrap items-end gap-x-2 gap-y-1';

export function HeaderInfo({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className={HEADER_LABEL}>{label}</p>
      <p className={HEADER_VALUE_ROW}>
        <span className="truncate text-sm font-semibold leading-5 text-slate-900">{value}</span>
      </p>
    </div>
  );
}

/**
 * Number input that round-trips `number | null` cleanly. Empty string ↔ null,
 * non-finite ↔ null, negative values clamp to 0 unless `allowNegative` is set
 * (temperature inputs opt in via the flag).
 */
export function NumberFieldInput({
  label,
  value,
  onChange,
  onBlur,
  allowNegative,
  disabled,
}: {
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
  /**
   * Fired when editing finishes. Use this rather than `onChange` for anything
   * that reacts to the value — typing "10" fires onChange twice, so a prompt or
   * a request on change would trigger on the intermediate "1".
   */
  onBlur?: () => void;
  allowNegative?: boolean;
  disabled?: boolean;
}) {
  return (
    <TextInput
      label={label}
      name={label}
      type="number"
      disabled={disabled}
      onBlur={onBlur}
      value={value == null ? '' : String(value)}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(null);
          return;
        }
        const n = Number(raw);
        if (!Number.isFinite(n)) {
          onChange(null);
          return;
        }
        if (!allowNegative && n < 0) {
          onChange(0);
          return;
        }
        onChange(n);
      }}
    />
  );
}

/** Wraps an input with a bracketed "cargo spec" hint to its right. */
export function InputWithSpecHint({
  children,
  spec,
}: {
  children: ReactNode;
  spec: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">{children}</div>
      <span className="shrink-0 whitespace-nowrap text-[11px] italic text-slate-400">({spec})</span>
    </div>
  );
}

/** Inline "LABEL: value" used inside the BookingTaskForm body. Italic + muted when empty. */
export function ReadOnlyField({
  label,
  value,
  className,
}: {
  label: string;
  value?: string | null;
  className?: string;
}) {
  const hasValue = Boolean(value && value.trim());
  return (
    <div className={className}>
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
        {label}:
      </span>{' '}
      <span className={'text-xs italic ' + (hasValue ? 'text-slate-700' : 'text-slate-400')}>
        {hasValue ? value : '—'}
      </span>
    </div>
  );
}

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  /** When true, render an X to clear the selection back to ''. */
  allowClear?: boolean;
  /** Locks the field — no dropdown, no clear, grayed out like a native disabled input. */
  disabled?: boolean;
}

/**
 * Lightweight typeahead / searchable select. Case-insensitive contains-match
 * on labels. Visual matches @my/ui's TextInput / SelectInput so the field
 * slots into the same form grids without alignment surprises.
 */
export function Combobox({
  label,
  value,
  onChange,
  options,
  placeholder,
  allowClear = false,
  disabled = false,
}: ComboboxProps) {
  const selected = options.find((o) => o.value === value) ?? null;
  const [query, setQuery] = useState<string>(selected?.label ?? '');
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  // Re-sync query to selected label when the value prop changes externally.
  useEffect(() => {
    if (!isFocused) setQuery(selected?.label ?? '');
  }, [selected, isFocused]);

  // Click-outside closes the dropdown AND reverts a half-typed query.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setIsFocused(false);
        setQuery(selected?.label ?? '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // While focused with the field still showing the selected value untouched,
    // list ALL options (so the operator can scroll / see every option, not just
    // the current match). Filtering kicks in as soon as they actually type.
    if (isFocused && selected && query === selected.label) return options;
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, isFocused, selected]);

  // When the dropdown opens, bring the selected option into view — helps long
  // lists like the quarter-hour time picker where the value sits far down.
  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  const commit = (opt: ComboboxOption) => {
    onChange(opt.value);
    setQuery(opt.label);
    setOpen(false);
    setIsFocused(false);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="w-full" ref={containerRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={(e) => {
            setIsFocused(true);
            setOpen(true);
            // Select the current text so typing replaces it cleanly.
            e.target.select();
          }}
          placeholder={placeholder ?? ' '}
          className={[
            'peer h-9 w-full rounded-sm border bg-white pb-1 pl-7 pr-7 pt-2 text-sm font-semibold text-slate-900',
            'placeholder:text-xs placeholder:font-normal placeholder:italic placeholder:text-slate-400',
            'focus:outline-none focus:ring-2',
            'border-slate-300 focus:ring-blue-500',
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
          ].join(' ')}
        />
        <label className="absolute -top-2 left-3 bg-white px-1 text-xs text-slate-500">
          {label}
        </label>
        {allowClear && value && !disabled && (
          <button
            type="button"
            // mousedown wins the race against input blur — otherwise the
            // dropdown re-opens before the clear runs.
            onMouseDown={(e) => {
              e.preventDefault();
              clear();
            }}
            aria-label="Clear selection"
            className="absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {open && (
        <div className="relative">
          <ul className="absolute left-0 right-0 top-1 z-20 max-h-56 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-md">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs italic text-slate-400">
                No matches — keep typing or pick from the full list.
              </li>
            ) : (
              filtered.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <li key={opt.value || '__empty'}>
                    <button
                      type="button"
                      ref={isSelected ? selectedRef : undefined}
                      // Same mousedown-vs-blur trick as the clear button.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        commit(opt);
                      }}
                      className={
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 ' +
                        (isSelected ? 'bg-purple-50 font-medium text-purple-700' : 'text-slate-700')
                      }
                    >
                      <span className="flex-1 truncate">{opt.label}</span>
                      {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-purple-600" />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Planned-time picker (quarter-hour) ───────────────────────────────────────

export const QUARTER_HOUR_OPTIONS: ComboboxOption[] = (() => {
  const opts: ComboboxOption[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const v = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      opts.push({ value: v, label: v });
    }
  }
  return opts;
})();
const QUARTER_HOUR_SET = new Set(QUARTER_HOUR_OPTIONS.map((o) => o.value));

/**
 * Planned-time picker constrained to quarter-hour slots (:00 / :15 / :30 / :45).
 * Native `<input type="time" step>` isn't honoured consistently across browsers
 * (some still list every minute), so we use the searchable Combobox — operators
 * can type "0930" to jump straight to it. A legacy off-grid value (an exact time
 * saved before this rule) is surfaced as an extra option so it still shows
 * rather than going blank; picking any slot snaps it back onto the grid.
 */
export function QuarterHourTimeInput({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const hhmm = (value ?? '').slice(0, 5);
  const options = useMemo(
    () =>
      hhmm && !QUARTER_HOUR_SET.has(hhmm)
        ? [{ value: hhmm, label: hhmm }, ...QUARTER_HOUR_OPTIONS]
        : QUARTER_HOUR_OPTIONS,
    [hhmm],
  );
  return (
    <Combobox
      label={label}
      value={hhmm}
      onChange={onChange}
      options={options}
      placeholder="--:--"
      allowClear
      disabled={disabled}
    />
  );
}
